import * as React from 'react';
import * as echarts from 'echarts/core';
import { BarChart } from 'echarts/charts';
import {
    GridComponent,
    LegendComponent,
    TooltipComponent,
} from 'echarts/components';
import { LabelLayout, UniversalTransition } from 'echarts/features';
import { SVGRenderer } from 'echarts/renderers';
import { format, parseISO } from 'date-fns';

echarts.use([
    GridComponent,
    LegendComponent,
    TooltipComponent,
    BarChart,
    LabelLayout,
    UniversalTransition,
    SVGRenderer,
]);

type Period = 'daily' | 'weekly' | 'monthly' | 'quarterly';

interface NewReturningConversionItem {
    period?: string;
    period_start?: string;
    new_visitors?: number;
    returning_visitors?: number;
    new_registered?: number;
    returning_registered?: number;
    new_conversion_rate_percent?: number;
    returning_conversion_rate_percent?: number;
}

interface TimingBucketItem {
    bucket?: string;
    registered_customers?: number;
    completed_profiles?: number;
}

interface ActiveDormantItem {
    bucket?: string;
    customers?: number;
    bucket_order?: number;
}

export const ACTIVE_DORMANT_BUCKET_SLUGS = [
    'active_7d',
    'active_8_30d',
    'inactive_31_90d',
    'inactive_90d_plus',
] as const;

export type ActiveDormantBucketSlug = typeof ACTIVE_DORMANT_BUCKET_SLUGS[number];

const formatPeriodLabel = (date: Date, period: Period, fallback: string) => {
    switch (period) {
        case 'daily':
            return format(date, 'MMM d');
        case 'weekly':
            return `W${format(date, 'II')} ${format(date, 'yyyy')}`;
        case 'monthly':
            return format(date, 'MMM yyyy');
        case 'quarterly':
            return `Q${Math.floor(date.getMonth() / 3) + 1} ${date.getFullYear()}`;
        default:
            return fallback;
    }
};

const parsePeriodDate = (value?: string) => {
    if (!value) {
        return new Date();
    }
    const parsed = parseISO(value);
    return isNaN(parsed.getTime()) ? new Date(value) : parsed;
};

const ChartRenderer: React.FC<{
    option: any;
    height?: number;
    onChartClick?: (params: any) => void;
}> = ({ option, height = 340, onChartClick }) => {
    const chartRef = React.useRef<HTMLDivElement>(null);
    const chartInstance = React.useRef<echarts.ECharts | null>(null);

    React.useEffect(() => {
        if (!chartRef.current) {
            return undefined;
        }

        if (!chartInstance.current) {
            chartInstance.current = echarts.init(chartRef.current);
        }

        chartInstance.current.setOption(option, true);

        const resizeHandler = () => chartInstance.current?.resize();
        window.addEventListener('resize', resizeHandler);
        return () => {
            window.removeEventListener('resize', resizeHandler);
        };
    }, [option]);

    React.useEffect(() => {
        const chart = chartInstance.current;
        if (!chart) {
            return undefined;
        }

        chart.off('click');
        if (onChartClick) {
            chart.on('click', onChartClick);
        }

        return () => {
            chart.off('click');
        };
    }, [onChartClick, option]);

    React.useEffect(() => () => {
        chartInstance.current?.dispose();
        chartInstance.current = null;
    }, []);

    return <div ref={chartRef} style={{ width: '100%', height }} />;
};

export const NewReturningVisitorConversionChart: React.FC<{
    data?: NewReturningConversionItem[];
    period: Period;
}> = ({ data = [], period }) => {
    const option = React.useMemo(() => {
        const categories: string[] = [];
        const newRates: number[] = [];
        const returningRates: number[] = [];

        data.forEach(item => {
            categories.push(formatPeriodLabel(parsePeriodDate(item.period_start), period, item.period || ''));
            newRates.push(Number(item.new_conversion_rate_percent) || 0);
            returningRates.push(Number(item.returning_conversion_rate_percent) || 0);
        });

        return {
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                formatter: (params: any) => {
                    const index = params?.[0]?.dataIndex || 0;
                    const row = data[index] || {};
                    return [
                        categories[index] || '',
                        `New visitors: ${(Number(row.new_visitors) || 0).toLocaleString()}`,
                        `New visitors who registered: ${(Number(row.new_registered) || 0).toLocaleString()} (${newRates[index] || 0}%)`,
                        `Returning visitors: ${(Number(row.returning_visitors) || 0).toLocaleString()}`,
                        `Returning visitors who registered: ${(Number(row.returning_registered) || 0).toLocaleString()} (${returningRates[index] || 0}%)`,
                    ].join('<br/>');
                },
            },
            legend: { data: ['New visitor registration rate', 'Returning visitor registration rate'], bottom: 0 },
            grid: { left: '6%', right: '6%', top: '10%', bottom: 86, containLabel: true },
            xAxis: { type: 'category', data: categories, axisLabel: { rotate: period === 'daily' ? 45 : 0 } },
            yAxis: { type: 'value', name: 'Registration rate', min: 0, max: 100, axisLabel: { formatter: '{value}%' } },
            series: [
                {
                    name: 'New visitor registration rate',
                    type: 'bar',
                    data: newRates,
                    itemStyle: { color: '#1890ff' },
                    barMaxWidth: 30,
                    label: { show: true, position: 'top', formatter: '{c}%', fontSize: 11 },
                },
                {
                    name: 'Returning visitor registration rate',
                    type: 'bar',
                    data: returningRates,
                    itemStyle: { color: '#52c41a' },
                    barMaxWidth: 30,
                    label: { show: true, position: 'top', formatter: '{c}%', fontSize: 11 },
                },
            ],
        };
    }, [data, period]);

    return <ChartRenderer option={option} height={360} />;
};

export const TimeToRegisterProfileChart: React.FC<{ data?: TimingBucketItem[] }> = ({ data = [] }) => {
    const option = React.useMemo(() => {
        const categories = data.map(item => item.bucket || 'Unknown');
        const registrations = data.map(item => Number(item.registered_customers) || 0);
        const profileCompletions = data.map(item => Number(item.completed_profiles) || 0);

        return {
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                formatter: (params: any) => {
                    const index = params?.[0]?.dataIndex || 0;
                    return [
                        categories[index] || '',
                        `Registered customers: ${(registrations[index] || 0).toLocaleString()}`,
                        `Completed profiles: ${(profileCompletions[index] || 0).toLocaleString()}`,
                    ].join('<br/>');
                },
            },
            legend: { data: ['Registered customers', 'Completed profiles'], bottom: 0 },
            grid: { left: '6%', right: '6%', top: '10%', bottom: 70, containLabel: true },
            xAxis: { type: 'category', data: categories },
            yAxis: { type: 'value', name: 'Customers' },
            series: [
                {
                    name: 'Registered customers',
                    type: 'bar',
                    data: registrations,
                    itemStyle: { color: '#1890ff' },
                    barMaxWidth: 34,
                    label: { show: true, position: 'top', formatter: (params: any) => Number(params.value || 0).toLocaleString(), fontSize: 11 },
                },
                {
                    name: 'Completed profiles',
                    type: 'bar',
                    data: profileCompletions,
                    itemStyle: { color: '#722ed1' },
                    barMaxWidth: 34,
                    label: { show: true, position: 'top', formatter: (params: any) => Number(params.value || 0).toLocaleString(), fontSize: 11 },
                },
            ],
        };
    }, [data]);

    return <ChartRenderer option={option} height={340} />;
};

export const ActiveDormantCustomersChart: React.FC<{
    data?: ActiveDormantItem[];
    onBarClick?: (bucket: ActiveDormantBucketSlug, label: string, count: number) => void;
}> = ({ data = [], onBarClick }) => {
    const handleChartClick = React.useCallback((params: any) => {
        if (!onBarClick || params?.componentType !== 'series') {
            return;
        }

        const index = Number(params.dataIndex);
        const slug = ACTIVE_DORMANT_BUCKET_SLUGS[index];
        const row = data[index];
        if (!slug || !row) {
            return;
        }

        onBarClick(slug, row.bucket || slug, Number(row.customers) || 0);
    }, [data, onBarClick]);

    const option = React.useMemo(() => {
        const categories = data.map(item => item.bucket || 'Unknown');
        const values = data.map(item => Number(item.customers) || 0);

        return {
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                formatter: (params: any) => {
                    const index = params?.[0]?.dataIndex || 0;
                    const lines = [
                        categories[index] || '',
                        `Customers: ${(values[index] || 0).toLocaleString()}`,
                    ];
                    if (onBarClick && values[index] > 0) {
                        lines.push('Click to view customers');
                    }
                    return lines.join('<br/>');
                },
            },
            grid: { left: '6%', right: '6%', top: '10%', bottom: 52, containLabel: true },
            xAxis: { type: 'category', data: categories, axisLabel: { rotate: 20 } },
            yAxis: { type: 'value', name: 'Customers' },
            series: [
                {
                    name: 'Customers',
                    type: 'bar',
                    data: values,
                    cursor: onBarClick ? 'pointer' : 'default',
                    itemStyle: {
                        color: (params: any) => {
                            const colors = ['#52c41a', '#1890ff', '#faad14', '#f5222d'];
                            return colors[params.dataIndex] || '#1890ff';
                        },
                    },
                    barMaxWidth: 42,
                    label: { show: true, position: 'top', formatter: (params: any) => Number(params.value || 0).toLocaleString(), fontSize: 11 },
                },
            ],
        };
    }, [data, onBarClick]);

    return <ChartRenderer option={option} height={340} onChartClick={onBarClick ? handleChartClick : undefined} />;
};
